/**
 * AdminDemoAccounts.jsx — admin creates time-limited demo accounts for prospects.
 *
 * Flow:
 *   1. Admin fills email + name + role + duration → click Create.
 *   2. Server returns a temp password + "share text" the admin copies and pastes into WhatsApp/email.
 *   3. Prospect logs in at oodel.co/login → forced to change password → lands on the dashboard.
 *   4. Account auto-expires after N days; admin can Extend or Convert-to-real.
 */
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import api from "../lib/api";

const StatusPill = ({ isExpired }) =>
  isExpired
    ? <span className="bg-red-100 text-red-800 text-[10px] px-1.5 py-0.5 font-mono uppercase">expired</span>
    : <span className="bg-green-100 text-green-800 text-[10px] px-1.5 py-0.5 font-mono uppercase">active</span>;

export function AdminDemoAccounts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    email: "", name: "", company: "", role: "enterprise",
    duration_days: 14, grant_private_vault: false,
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);   // post-create modal

  const load = () => api.get("/admin/demo-accounts").then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.name) return toast.error("Email and name are required.");
    setBusy(true);
    try {
      const r = await api.post("/admin/demo-accounts", form);
      setCredentials(r.data);
      setForm({ ...form, email: "", name: "", company: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const extend = async (uid) => {
    const days = parseInt(window.prompt("Extend by how many days?", "7") || "0", 10);
    if (!days) return;
    try {
      await api.post(`/admin/demo-accounts/${uid}/extend`, { extra_days: days });
      toast.success(`Extended by ${days} day${days === 1 ? "" : "s"}.`);
      load();
    } catch (err) { toast.error("Extend failed"); }
  };

  const convert = async (uid) => {
    if (!window.confirm("Convert this demo to a real paying account? Their data is preserved, the expiry is removed, and they keep their current access flags.")) return;
    try {
      await api.post(`/admin/demo-accounts/${uid}/convert`);
      toast.success("Converted to real account.");
      load();
    } catch (err) { toast.error("Convert failed"); }
  };

  const del = async (uid, email) => {
    if (!window.confirm(`Permanently delete the demo account ${email}? All their activity will be wiped.`)) return;
    try {
      await api.delete(`/admin/demo-accounts/${uid}`);
      toast.success("Deleted.");
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(credentials.share_text);
      toast.success("Copied to clipboard — paste into WhatsApp / email now.");
    } catch { toast.error("Copy failed — please select manually."); }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-demo-accounts">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · SALES</div>
        <h1 className="text-4xl font-black tracking-tighter">Demo accounts</h1>
        <p className="text-sm text-neutral-500 mt-1">Create time-limited demo accounts to give prospective subscribers and enterprise clients full access to the National Intelligence Hub before they buy. Accounts auto-expire — you can extend, convert to real, or delete.</p>
      </div>

      {/* Create form */}
      <form onSubmit={create} className="bg-white border-2 border-black p-5 mb-6 hard-shadow space-y-3" data-testid="create-demo">
        <div className="overline mb-1">+ NEW DEMO ACCOUNT</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Email *</Label><Input data-testid="demo-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="clientname@company.com" className="rounded-none" /></div>
          <div><Label>Full name *</Label><Input data-testid="demo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ahmad Khan" className="rounded-none" /></div>
          <div><Label>Company</Label><Input data-testid="demo-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Unilever Pakistan" className="rounded-none" /></div>
          <div>
            <Label>Demo type</Label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-neutral-300 p-2 rounded-none" data-testid="demo-role">
              <option value="subscriber">Subscriber (aggregate dashboards only)</option>
              <option value="enterprise">Enterprise (+ Audience Explorer + raw exports)</option>
            </select>
          </div>
          <div><Label>Duration (days)</Label><Input data-testid="demo-duration" type="number" min="1" max="180" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 14 })} className="rounded-none" /></div>
          {form.role === "enterprise" && (
            <label className="flex items-center gap-2 text-sm pt-6">
              <Switch checked={form.grant_private_vault} onCheckedChange={(v) => setForm({ ...form, grant_private_vault: v })} data-testid="demo-vault" />
              Also grant access to private vault (internal/custom dashboards)
            </label>
          )}
        </div>
        <Button type="submit" disabled={busy} className="bg-mango hover:bg-mango/90 text-white rounded-none" data-testid="demo-create">
          {busy ? "Creating…" : "Create demo account →"}
        </Button>
      </form>

      {/* List */}
      <div className="space-y-1">
        {!loading && rows.length === 0 && <div className="bg-white p-12 text-center text-neutral-500 italic border border-dashed">No demo accounts yet.</div>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-neutral-200 p-3 flex items-center justify-between gap-3 text-sm" data-testid={`demo-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{r.name} <span className="text-neutral-500 font-normal">· {r.email}</span></div>
              <div className="text-xs text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                <StatusPill isExpired={r.is_expired} />
                <code className="bg-neutral-100 px-1">{r.role}</code>
                {r.company && <span>· {r.company}</span>}
                <span>· expires {r.demo_expires_at?.slice(0, 10)}</span>
                {r.has_private_vault_access && <span className="bg-mango/10 text-mango px-1.5 py-0.5">Vault</span>}
              </div>
            </div>
            <div className="flex gap-2 text-xs shrink-0">
              <button onClick={() => extend(r.id)} className="text-mango hover:underline font-bold" data-testid={`extend-${r.id}`}>Extend</button>
              <button onClick={() => convert(r.id)} className="text-blue-600 hover:underline" data-testid={`convert-${r.id}`}>Convert →</button>
              <button onClick={() => del(r.id, r.email)} className="text-red-600 hover:underline" data-testid={`del-${r.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Post-create credentials modal */}
      {credentials && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="demo-credentials">
          <div className="bg-white max-w-xl w-full p-6 border-2 border-mango hard-shadow">
            <div className="overline text-mango mb-2">DEMO ACCOUNT CREATED</div>
            <h2 className="text-xl font-bold mb-3">Share these credentials with your client</h2>
            <pre className="bg-neutral-100 p-3 text-xs font-mono whitespace-pre-wrap break-words mb-4">{credentials.share_text}</pre>
            <p className="text-xs text-neutral-500 mb-4">⚠️ This is the only time the temporary password will be shown — copy it now.</p>
            <div className="flex gap-2">
              <Button onClick={copy} className="bg-mango hover:bg-mango/90 text-white rounded-none">Copy to clipboard</Button>
              <Button variant="outline" onClick={() => setCredentials(null)} className="rounded-none">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
